import { useRef } from "react";
import { FileDown, ArrowLeft } from "lucide-react";

const features = [
  {
    title: "Multi-Role Dashboards",
    desc: "Purpose-built dashboards for Admin, Operations, QC, Agents, Vendors, Client HR & Employees — each with role-specific views and actions.",
    icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1"
  },
  {
    title: "End-to-End Case Management",
    desc: "Automated case IDs, full lifecycle tracking (Draft to Completed), real-time status & colour-coded results (Clear / Amber / Red).",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
  },
  {
    title: "10 Verification Types",
    desc: "Employment, Education, Address (Field), Criminal Court Records, ID Check, Reference Check, Drug Test, Global Database, Credit Check & Document Verification — all configurable via packages.",
    icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
  },
  {
    title: "Automated PDF Reports",
    desc: "Professional, branded verification reports generated automatically on case completion — with annexures, check results & overall assessment.",
    icon: "M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
  },
  {
    title: "Smart Email Automation",
    desc: "Auto-triggered completion emails with PDF attachments, configurable templates, bulk reminders, and a 6-tier email fallback to never miss a recipient.",
    icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
  },
  {
    title: "Insufficiency Tracking",
    desc: "Raise, track & resolve insufficiencies with ageing indicators (Red >30 days, Amber >15 days), selective candidate emails & consolidated views.",
    icon: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
  },
  {
    title: "Candidate Self-Service Portal",
    desc: "Secure invitation links for employees to fill their own verification details, upload documents, sign declarations digitally & track progress.",
    icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
  },
  {
    title: "Operations Command Center",
    desc: "Team workload views, auto-assign rules, TAT breach monitoring, check reassignment & returned-checks management — all in one place.",
    icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z"
  },
  {
    title: "QC & Quality Assurance",
    desc: "Dedicated review queue, check-wise pending view, approval/rejection workflows, bulk QC submission & quick-approve-and-assign.",
    icon: "M5 13l4 4L19 7"
  },
  {
    title: "Bulk Operations Engine",
    desc: "Bulk employee import via Excel, AI-powered document matching, bulk vendor assignment, bulk email dispatch & bulk QC approval.",
    icon: "M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7zm5 0h6m-6 4h6m-6 4h4"
  },
  {
    title: "Smart Auto-Initiation",
    desc: "Growing company & institution contact database enables automatic verification email dispatch — reducing manual effort significantly.",
    icon: "M13 10V3L4 14h7v7l9-11h-7z"
  },
  {
    title: "Secure Cloud Storage",
    desc: "All documents and annexures stored in encrypted cloud Object Storage — resilient, accessible & never lost during server maintenance.",
    icon: "M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
  },
  {
    title: "Real-Time Chat & Notifications",
    desc: "Built-in team messaging, announcement banners, real-time notification bell & configurable alerts to keep everyone in sync.",
    icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
  },
  {
    title: "MIS & Scheduled Reporting",
    desc: "Automated daily MIS, weekly insufficiency reports, candidate reminder automation & vendor pending alerts — all configurable.",
    icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
  },
  {
    title: "Complete Audit Trail",
    desc: "Every action logged — status changes, assignments, emails, uploads. Exportable PDF audit trail for compliance & transparency.",
    icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
  },
  {
    title: "SMS Integration",
    desc: "Individual & bulk SMS to candidates and clients with configurable templates, delivery logs & tracking.",
    icon: "M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
  },
  {
    title: "B2B & External API",
    desc: "Dedicated B2B agency module with API access for enterprise integrations, webhook support & real-time case status queries.",
    icon: "M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
  },
  {
    title: "Extra Cost & Invoicing",
    desc: "Request, approve & track additional costs per check. Vendor invoice generation with status tracking (Pending / Approved / Paid).",
    icon: "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
  },
];

const stats = [
  { value: "200+", label: "API Endpoints" },
  { value: "18+", label: "Dashboards" },
  { value: "10", label: "Verification Types" },
  { value: "20+", label: "Automated Workflows" },
];

function FeatureIcon({ d }) {
  return (
    <svg className="ftr-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

export default function FeatureShowcase() {
  const printRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .ftr-page {
          font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
          background: #f8f9fb;
          min-height: 100vh;
          color: #1a1d23;
        }

        /* ---------- TOOLBAR (hidden in print) ---------- */
        .ftr-toolbar {
          position: fixed; top: 0; left: 0; right: 0; z-index: 50;
          background: rgba(255,255,255,.85); backdrop-filter: blur(12px);
          border-bottom: 1px solid #e5e7eb;
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 32px;
        }
        .ftr-toolbar-left { display:flex; align-items:center; gap:12px; }
        .ftr-toolbar-left a { color:#6b7280; display:flex; align-items:center; text-decoration:none; font-size:14px; }
        .ftr-toolbar-left a:hover { color:#1a1d23; }
        .ftr-toolbar h3 { font-size:15px; font-weight:600; color:#1a1d23; margin:0; }
        .ftr-btn-pdf {
          display:inline-flex; align-items:center; gap:8px;
          background: #1a56db; color:#fff; border:none; border-radius:8px;
          padding: 9px 20px; font-size:14px; font-weight:600; cursor:pointer;
          transition: background .15s;
        }
        .ftr-btn-pdf:hover { background: #1544b5; }

        /* ---------- HERO ---------- */
        .ftr-hero {
          background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%);
          color: #fff; text-align: center;
          padding: 80px 32px 60px;
          margin-top: 56px;
        }
        .ftr-hero-badge {
          display:inline-block; background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.2);
          border-radius:20px; padding:6px 18px; font-size:12px; font-weight:600;
          letter-spacing:.8px; text-transform:uppercase; margin-bottom:20px; color:#93c5fd;
        }
        .ftr-hero h1 {
          font-size: 42px; font-weight: 800; line-height: 1.15; margin: 0 auto 16px;
          max-width: 700px;
        }
        .ftr-hero h1 span { color: #60a5fa; }
        .ftr-hero p {
          font-size: 17px; line-height: 1.6; color: #cbd5e1;
          max-width: 620px; margin: 0 auto;
        }

        /* ---------- STATS BAR ---------- */
        .ftr-stats {
          display: grid; grid-template-columns: repeat(4, 1fr);
          max-width: 820px; margin: -32px auto 0; position: relative; z-index: 2;
          background: #fff; border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0,0,0,.08);
          padding: 28px 0;
        }
        .ftr-stat { text-align: center; border-right: 1px solid #f0f0f0; }
        .ftr-stat:last-child { border-right: none; }
        .ftr-stat-val { font-size: 32px; font-weight: 800; color: #1a56db; }
        .ftr-stat-lbl { font-size: 13px; color: #6b7280; margin-top: 2px; font-weight: 500; }

        /* ---------- FEATURES GRID ---------- */
        .ftr-section {
          max-width: 1100px; margin: 0 auto;
          padding: 64px 32px 80px;
        }
        .ftr-section-title {
          text-align: center; font-size: 28px; font-weight: 700; margin-bottom: 8px;
        }
        .ftr-section-sub {
          text-align: center; font-size: 15px; color: #6b7280; margin-bottom: 48px;
          max-width: 560px; margin-left: auto; margin-right: auto;
        }
        .ftr-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
        }
        .ftr-card {
          background: #fff; border-radius: 14px;
          padding: 28px 24px;
          border: 1px solid #eef0f4;
          transition: box-shadow .2s, transform .2s;
        }
        .ftr-card:hover {
          box-shadow: 0 8px 32px rgba(0,0,0,.07);
          transform: translateY(-2px);
        }
        .ftr-icon {
          width: 36px; height: 36px; color: #1a56db;
          margin-bottom: 14px;
        }
        .ftr-card h3 {
          font-size: 16px; font-weight: 700; margin: 0 0 8px; color: #1a1d23;
        }
        .ftr-card p {
          font-size: 14px; line-height: 1.65; color: #4b5563; margin: 0;
        }

        /* ---------- FOOTER ---------- */
        .ftr-footer {
          text-align: center; padding: 40px 32px;
          border-top: 1px solid #e5e7eb;
          color: #9ca3af; font-size: 13px;
        }
        .ftr-footer strong { color: #1a1d23; font-weight: 700; }

        /* ---------- PRINT STYLES ---------- */
        @media print {
          .ftr-toolbar { display: none !important; }
          .ftr-hero { margin-top: 0; padding: 48px 24px 40px; }
          .ftr-hero h1 { font-size: 32px; }
          .ftr-stats { margin-top: -20px; box-shadow: none; border: 1px solid #ddd; }
          .ftr-section { padding: 40px 16px 48px; }
          .ftr-grid { gap: 16px; }
          .ftr-card { break-inside: avoid; box-shadow: none; border: 1px solid #ddd; padding: 18px 16px; }
          .ftr-card:hover { transform: none; box-shadow: none; }
          .ftr-page { background: #fff; }
          @page { margin: 0.6in; }
        }
      `}</style>

      <div className="ftr-page" ref={printRef}>
        {/* Toolbar */}
        <div className="ftr-toolbar">
          <div className="ftr-toolbar-left">
            <a href="/" title="Back to Dashboard"><ArrowLeft size={18}/></a>
            <h3>Feature Overview</h3>
          </div>
          <button className="ftr-btn-pdf" onClick={handlePrint} data-testid="download-pdf-btn">
            <FileDown size={16}/> Save as PDF
          </button>
        </div>

        {/* Hero */}
        <div className="ftr-hero">
          <div className="ftr-hero-badge">Platform Capabilities</div>
          <img src="/justverified-logo-full.jpg" alt="JustVerified" style={{width: '180px', margin: '0 auto 20px', display: 'block', borderRadius: '8px'}} />
          <h1>
            Background Verification Platform
          </h1>
          <p>
            A comprehensive, cloud-based BGV management system that automates verification workflows,
            generates branded PDF reports, and keeps every stakeholder — from HR to vendors — on the same page.
          </p>
        </div>

        {/* Stats */}
        <div className="ftr-stats">
          {stats.map((s,i) => (
            <div className="ftr-stat" key={i}>
              <div className="ftr-stat-val">{s.value}</div>
              <div className="ftr-stat-lbl">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="ftr-section">
          <div className="ftr-section-title">Everything You Need, Built In</div>
          <p className="ftr-section-sub">
            From candidate onboarding to final report delivery — every step of the BGV lifecycle is covered.
          </p>
          <div className="ftr-grid">
            {features.map((f,i) => (
              <div className="ftr-card" key={i} data-testid={`feature-card-${i}`}>
                <FeatureIcon d={f.icon}/>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="ftr-footer">
          <strong>JustVerified</strong> &mdash; Trusted Background Verification Management<br/>
          &copy; {new Date().getFullYear()} JustVerified. All rights reserved.
        </div>
      </div>
    </>
  );
}
